import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { parseDate, formatDateBR } from "../utils/dateUtils";
import StudentAreaModal from "../components/StudentAreaModal";
import {
  Users,
  Heart,
  Calendar,
  Cake,
  Play,
  ChevronRight,
  Clock,
  Music,
  ExternalLink,
  Brain,
  GraduationCap,
  Dumbbell,
  ClipboardList,
  HandHeart,
  Shirt,
  Package,
  Apple,
  Cross,
  FileText,
  Video,
  BookOpen,
  ArrowUpDown,
  SortAsc,
  HeartHandshake,
  MessageCircle,
  AlertTriangle
} from "lucide-react";
import { Progress } from "../components/ui/progress";
import { waLink } from "../components/ConnectPersonModal";
import { ProgramacaoCalendar } from "../components/ProgramacaoCalendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping for custom project icons
const PROJECT_ICONS = {
  "default": null,
  "music": Music,
  "heart": Heart,
  "hand-heart": HandHeart,
  "shirt": Shirt,
  "package": Package,
  "apple": Apple,
  "brain": Brain,
  "graduation": GraduationCap,
  "dumbbell": Dumbbell,
  "cross": Cross,
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAdmin, currentBranch } = useAuth();
  const [stats, setStats] = useState({});
  const [banner, setBanner] = useState({});
  const [mySchedules, setMySchedules] = useState([]);
  const [activeProjects, setActiveProjects] = useState([]);
  const [projectsOrder, setProjectsOrder] = useState("nome"); // nome, tipo, recentes
  const [loading, setLoading] = useState(true);
  const [connSummary, setConnSummary] = useState(null);
  const [myConn, setMyConn] = useState(null);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [scheduleDetailDialog, setScheduleDetailDialog] = useState(false);
  const [highlightedScheduleId, setHighlightedScheduleId] = useState(null);
  
  // State for song details modal
  const [selectedSong, setSelectedSong] = useState(null);
  const [songDetailDialog, setSongDetailDialog] = useState(false);
  
  // State for project participants modal
  const [selectedProjectForParticipants, setSelectedProjectForParticipants] = useState(null);
  const [participantsDialog, setParticipantsDialog] = useState(false);
  
  // State for Student/Member Area modal
  const [studentAreaModalOpen, setStudentAreaModalOpen] = useState(false);
  const [studentAreaProject, setStudentAreaProject] = useState(null);
  
  // Check permission for Social module
  const canAccessSocialModule = () => {
    if (isAdmin()) return true;
    return user?.permissoes?.social?.ver === true;
  };

  // Check permission to view all schedules
  const canViewAllSchedules = () => {
    if (isAdmin()) return true;
    return user?.permissoes?.ver_todas_escalas === true;
  };

  // Check if user can view project participants (is leader or has permission)
  const canViewProjectParticipants = (project) => {
    if (isAdmin()) return true;
    if (user?.permissoes?.social?.ver_todos_projetos === true) return true;
    // Check if user is one of the leaders
    if (project?.lideres?.some(l => l.id === user?.pessoa_id)) return true;
    if (project?.lider_id === user?.pessoa_id) return true;
    return false;
  };

  // Check permission to create/manage schedules (for "Ver todas" button)
  const canManageSchedules = () => {
    if (isAdmin()) return true;
    return user?.permissoes?.escalas?.criar === true || user?.permissoes?.escalas?.editar === true;
  };

  // Check if user can access the Escalas page (ver permission)
  const canAccessSchedulesPage = () => {
    if (isAdmin()) return true;
    return user?.permissoes?.escalas?.ver === true;
  };

  // Check for escala parameter in URL
  useEffect(() => {
    const escalaId = searchParams.get("escala");
    if (escalaId) {
      setHighlightedScheduleId(escalaId);
      // Auto-scroll and open the schedule detail after data loads
    }
  }, [searchParams]);

  // Auto-open highlighted schedule when data loads
  useEffect(() => {
    if (highlightedScheduleId && mySchedules.length > 0) {
      const schedule = mySchedules.find(s => s.id === highlightedScheduleId);
      if (schedule) {
        setSelectedSchedule(schedule);
        setScheduleDetailDialog(true);
        // Clear the highlight after opening
        setTimeout(() => {
          setHighlightedScheduleId(null);
          // Clean URL parameter
          navigate("/dashboard", { replace: true });
        }, 1000);
      }
    }
  }, [highlightedScheduleId, mySchedules, navigate]);

  // Get dashboard card visibility from user permissions
  const getDashboardCardVisibility = (cardKey) => {
    // Admin always sees all cards
    if (isAdmin()) return true;
    
    // Check user's profile permissions for dashboard_cards
    const dashboardCards = user?.permissoes?.dashboard_cards;
    if (!dashboardCards) return true; // Default: show all cards
    
    return dashboardCards[cardKey] !== false;
  };

  const showStatisticsCards = getDashboardCardVisibility("estatisticas");
  const showSchedulesWidget = getDashboardCardVisibility("proximas_escalas");
  const showBirthdaysWidget = getDashboardCardVisibility("aniversariantes") || getDashboardCardVisibility("estatisticas");

  useEffect(() => {
    if (user) {
      loadDashboardData();
      axios.get(`${API}/connections/summary`).then(r => setConnSummary(r.data)).catch(() => {});
      axios.get(`${API}/connections/my`).then(r => setMyConn(r.data)).catch(() => {});
    }
  }, [currentBranch, user]);

  const loadDashboardData = async () => {
    try {
      // Build filial parameter
      const filialParam = currentBranch ? `?filial_id=${currentBranch.id}` : '';
      
      // Determine which schedules endpoint to use based on permissions
      const schedulesEndpoint = canViewAllSchedules() 
        ? `${API}/schedules${filialParam}` 
        : `${API}/schedules/my${filialParam}`;
      
      const [statsRes, bannerRes, schedulesRes, projectsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/dashboard/banner`),
        axios.get(schedulesEndpoint),
        axios.get(`${API}/projects/active`).catch(() => ({ data: [] }))
      ]);
      
      setStats(statsRes.data);
      setBanner(bannerRes.data);
      
      // Filter out past schedules (before today) and sort ascending (upcoming first)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const upcomingSchedules = (schedulesRes.data || [])
        .filter(s => {
          if (!s.evento_data) return true; // Keep schedules without date
          // Parse date properly to avoid timezone issues
          const dateStr = s.evento_data.split('T')[0];
          const [year, month, day] = dateStr.split('-').map(Number);
          const scheduleDate = new Date(year, month - 1, day);
          scheduleDate.setHours(0, 0, 0, 0);
          return scheduleDate.getTime() >= today.getTime(); // Today or future
        })
        .sort((a, b) => {
          // Sort ascending (earliest first)
          const dateA = a.evento_data ? new Date(a.evento_data) : new Date(9999, 11, 31);
          const dateB = b.evento_data ? new Date(b.evento_data) : new Date(9999, 11, 31);
          return dateA - dateB;
        })
        .slice(0, 5);
      
      setMySchedules(upcomingSchedules);
      setActiveProjects(projectsRes.data);
    } catch (error) {
      console.error("Dashboard error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleClick = (schedule) => {
    setSelectedSchedule(schedule);
    setScheduleDetailDialog(true);
  };

  const handleGoToSchedules = () => {
    setScheduleDetailDialog(false);
    navigate("/escalas");
  };

  const handleSongClick = async (songId) => {
    try {
      const res = await axios.get(`${API}/songs/${songId}`);
      setSelectedSong(res.data);
      setSongDetailDialog(true);
    } catch (error) {
      toast.error("Erro ao carregar detalhes da música");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto" data-testid="dashboard">
      {/* Banner Section */}
      <div className="relative rounded-2xl overflow-hidden shadow-lg bg-muted">
        {banner.tipo === "video" ? (
          <div className="aspect-[21/9] sm:aspect-[3/1] bg-black">
            <iframe
              src={banner.url}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="Banner Video"
            />
          </div>
        ) : (
          <div className="relative aspect-[21/9] sm:aspect-[3/1] min-h-[120px]">
            <img
              src={banner.url || "https://images.unsplash.com/photo-1577733563933-0d02086b1a82?w=1200"}
              alt="Banner"
              className="w-full h-full object-cover object-center"
              style={{ objectFit: 'cover', objectPosition: 'center' }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://images.unsplash.com/photo-1577733563933-0d02086b1a82?w=1200";
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            {banner.titulo && (
              <div className="absolute bottom-3 left-3 right-3 md:bottom-4 md:left-4 md:right-4">
                <h2 className="text-white text-base md:text-xl font-bold line-clamp-1">
                  {banner.titulo}
                </h2>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Stats Cards */}
      {showStatisticsCards && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-hover" data-testid="stat-membros">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-primary/10">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_membros || 0}</p>
                  <p className="text-xs text-muted-foreground">Membros</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="stat-voluntarios">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-green-500/10">
                  <Heart className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_voluntarios || 0}</p>
                  <p className="text-xs text-muted-foreground">Voluntários</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="stat-eventos">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-orange-500/10">
                  <Calendar className="h-5 w-5 text-orange-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.proximos_eventos || 0}</p>
                  <p className="text-xs text-muted-foreground">Eventos</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="stat-aniversariantes">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-pink-500/10">
                  <Cake className="h-5 w-5 text-pink-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.aniversariantes || 0}</p>
                  <p className="text-xs text-muted-foreground">Aniversários</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Calendário de Programação */}
      <ProgramacaoCalendar />

      {/* Igreja Conectada - card do líder */}
      {connSummary?.can_view && connSummary.total > 0 && (
        <Card
          className="card-hover cursor-pointer border-primary/20 bg-gradient-to-br from-primary/5 to-transparent"
          onClick={() => navigate("/conexoes?status=nao_conectado")}
          data-testid="igreja-conectada-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-2xl bg-primary/10 shrink-0">
                <HeartHandshake className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold text-sm">Igreja Conectada</p>
                  <span className="text-lg font-bold text-primary" data-testid="igreja-conectada-percent">{connSummary.percentual}%</span>
                </div>
                <Progress value={connSummary.percentual} className="h-2 my-1.5" />
                <div className="flex items-center gap-3 text-xs">
                  <button
                    className="text-green-700 hover:underline"
                    onClick={(e) => { e.stopPropagation(); navigate("/conexoes?status=conectado"); }}
                    data-testid="igreja-conectada-connected"
                  >
                    <span className="font-bold">{connSummary.conectados}</span> conectados
                  </button>
                  <button
                    className="text-orange-700 hover:underline"
                    onClick={(e) => { e.stopPropagation(); navigate("/conexoes?status=nao_conectado"); }}
                    data-testid="igreja-conectada-not-connected"
                  >
                    <span className="font-bold">{connSummary.nao_conectados}</span> não conectados
                  </button>
                  {connSummary.alertas_sexo > 0 && (
                    <span className="flex items-center gap-1 text-amber-600"><AlertTriangle className="h-3 w-3" />{connSummary.alertas_sexo}</span>
                  )}
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Você está conectado com... (membro) */}
      {myConn?.conectores?.length > 0 && (
        <Card data-testid="meus-conectores-card">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5"><HeartHandshake className="h-3.5 w-3.5 text-green-600" /> Você está conectado com</p>
            <div className="flex flex-wrap gap-2">
              {myConn.conectores.map(c => (
                <div key={c.id} className="flex items-center gap-2 pl-3 pr-1 py-1 rounded-full border bg-card" data-testid={`conector-${c.id}`}>
                  <div>
                    <p className="text-sm font-medium leading-tight">{c.nome}</p>
                    <p className="text-[10px] text-muted-foreground leading-tight">{[...new Set(c.origens)].join(", ")}</p>
                  </div>
                  {waLink(c.telefone) ? (
                    <a href={waLink(c.telefone)} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-full bg-green-100 text-green-700 hover:bg-green-200 transition-colors" title="Falar no WhatsApp" data-testid={`conector-whatsapp-${c.id}`}>
                      <MessageCircle className="h-4 w-4" />
                    </a>
                  ) : <span className="w-2" />}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* My Schedules Widget */}
      {showSchedulesWidget && (
        <Card data-testid="minhas-escalas-widget">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                {canViewAllSchedules() ? "Próximas Escalas" : "Minhas Escalas"}
              </CardTitle>
              {canAccessSchedulesPage() && (
                <Button variant="ghost" size="sm" className="text-primary" onClick={() => navigate("/escalas")} data-testid="view-all-schedules">
                  Ver todas <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {mySchedules.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                {canViewAllSchedules() ? "Nenhuma escala cadastrada" : "Você não está escalado(a) em nenhum evento"}
              </p>
            ) : (
              mySchedules.map((schedule) => (
                <div
                  key={schedule.id}
                  className={`flex items-center gap-4 p-3 rounded-xl transition-colors cursor-pointer ${
                    highlightedScheduleId === schedule.id 
                      ? "bg-primary/20 ring-2 ring-primary animate-pulse" 
                      : "bg-muted/50 hover:bg-muted"
                  }`}
                  onClick={() => handleScheduleClick(schedule)}
                  data-testid={`schedule-item-${schedule.id}`}
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex flex-col items-center justify-center">
                    <span className="text-xs text-muted-foreground">
                      {schedule.evento_data ? format(parseDate(schedule.evento_data), "MMM", { locale: ptBR }).toUpperCase() : "---"}
                    </span>
                    <span className="text-lg font-bold text-primary">
                      {schedule.evento_data ? format(parseDate(schedule.evento_data), "dd") : "--"}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{schedule.nome || "Escala"}</p>
                    <p className="text-sm text-muted-foreground truncate">{schedule.evento_nome || "Evento não definido"}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="secondary" className="text-xs">
                        {schedule.evento_horario || "Horário não definido"}
                      </Badge>
                      {schedule.minha_funcao && (
                        <Badge variant="outline" className="text-xs">
                          {schedule.minha_funcao}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </div>
              ))
            )}
          </CardContent>
        </Card>
      )}

      {/* Active Projects Section - Grouped by Type */}
      {activeProjects.length > 0 && (
        <div className="space-y-4" data-testid="projects-widget">
          {/* Group projects by type */}
          {(() => {
            // Helper functions inside the render
            const getProjectIcon = (tipo, customIcon) => {
              if (customIcon && customIcon !== "default" && PROJECT_ICONS[customIcon]) {
                const CustomIcon = PROJECT_ICONS[customIcon];
                return <CustomIcon className="h-5 w-5" />;
              }
              switch (tipo) {
                case "evento": return <Calendar className="h-5 w-5" />;
                case "inscricao": return <ClipboardList className="h-5 w-5" />;
                case "musica": return <Music className="h-5 w-5" />;
                case "doacoes": return <Heart className="h-5 w-5" />;
                case "terapias": return <Brain className="h-5 w-5" />;
                case "cursos": return <GraduationCap className="h-5 w-5" />;
                case "esporte": return <Dumbbell className="h-5 w-5" />;
                case "oracao": return <HandHeart className="h-5 w-5" />;
                default: return <Heart className="h-5 w-5" />;
              }
            };
            
            const getProjectColors = (tipo) => {
              switch (tipo) {
                case "evento": return { bg: "bg-amber-500", light: "bg-amber-100 dark:bg-amber-900/30", text: "text-amber-600" };
                case "inscricao": return { bg: "bg-teal-500", light: "bg-teal-100 dark:bg-teal-900/30", text: "text-teal-600" };
                case "musica": return { bg: "bg-purple-500", light: "bg-purple-100 dark:bg-purple-900/30", text: "text-purple-600" };
                case "doacoes": return { bg: "bg-rose-500", light: "bg-rose-100 dark:bg-rose-900/30", text: "text-rose-600" };
                case "terapias": return { bg: "bg-sky-500", light: "bg-sky-100 dark:bg-sky-900/30", text: "text-sky-600" };
                case "cursos": return { bg: "bg-emerald-500", light: "bg-emerald-100 dark:bg-emerald-900/30", text: "text-emerald-600" };
                case "esporte": return { bg: "bg-orange-500", light: "bg-orange-100 dark:bg-orange-900/30", text: "text-orange-600" };
                case "oracao": return { bg: "bg-indigo-500", light: "bg-indigo-100 dark:bg-indigo-900/30", text: "text-indigo-600" };
                default: return { bg: "bg-primary", light: "bg-primary/10", text: "text-primary" };
              }
            };
            
            const getTypeName = (tipo) => {
              const names = {
                evento: "Eventos",
                inscricao: "Inscrições",
                musica: "Música",
                doacoes: "Doações",
                terapias: "Terapias",
                cursos: "Cursos",
                esporte: "Esportes",
                oracao: "Pedidos de Oração"
              };
              return names[tipo] || tipo?.charAt(0).toUpperCase() + tipo?.slice(1);
            };
            
            // Group projects by bloco_inscricao (if defined) or by tipo
            const projectsByBloco = activeProjects.reduce((acc, project) => {
              // Use bloco_inscricao if defined, otherwise use tipo
              const blocoKey = project.bloco_inscricao || `__tipo__${project.tipo || "outros"}`;
              if (!acc[blocoKey]) {
                acc[blocoKey] = {
                  nome: project.bloco_inscricao || getTypeName(project.tipo),
                  tipo: project.tipo, // Keep tipo for icon/color
                  isCustomBloco: !!project.bloco_inscricao,
                  projects: []
                };
              }
              acc[blocoKey].projects.push(project);
              return acc;
            }, {});
            
            // Sort blocos: custom blocos first (alphabetically), then type-based (by priority)
            const typeOrder = ["evento", "inscricao", "cursos", "musica", "esporte", "doacoes", "terapias", "oracao"];
            const sortedBlocos = Object.entries(projectsByBloco).sort(([keyA, blocoA], [keyB, blocoB]) => {
              // Custom blocos come first
              if (blocoA.isCustomBloco && !blocoB.isCustomBloco) return -1;
              if (!blocoA.isCustomBloco && blocoB.isCustomBloco) return 1;
              
              // If both are custom, sort alphabetically
              if (blocoA.isCustomBloco && blocoB.isCustomBloco) {
                return blocoA.nome.localeCompare(blocoB.nome);
              }
              
              // If both are type-based, use type priority
              const tipoA = blocoA.tipo || "outros";
              const tipoB = blocoB.tipo || "outros";
              const idxA = typeOrder.indexOf(tipoA);
              const idxB = typeOrder.indexOf(tipoB);
              return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
            });
            
            return sortedBlocos.map(([blocoKey, bloco]) => {
              // Sort projects within bloco by bloco_ordem, then by name
              const sortedProjects = [...bloco.projects].sort((a, b) => {
                const ordemA = a.bloco_ordem || 0;
                const ordemB = b.bloco_ordem || 0;
                if (ordemA !== ordemB) return ordemA - ordemB;
                return (a.nome || "").localeCompare(b.nome || "");
              });
              
              // Get color from first project's tipo
              const firstTipo = bloco.projects[0]?.tipo || "outros";
              const typeColors = getProjectColors(firstTipo);
              
              return (
                <Card key={blocoKey} className="border-0 shadow-lg overflow-hidden">
                  <CardHeader className={`pb-2 pt-3 px-4 ${typeColors.light}`}>
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg ${typeColors.bg} text-white flex items-center justify-center`}>
                        {getProjectIcon(firstTipo)}
                      </div>
                      {bloco.nome}
                      <Badge variant="secondary" className="ml-auto text-xs">
                        {bloco.projects.length}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 p-3">
                    {sortedProjects.map((project) => {
                      const isEnrolled = project.ja_inscrito;
                      const canEnroll = project.disponivel_inscricao && !isEnrolled;
                      const isOracao = project.tipo === "oracao";
                      const canViewParticipants = canViewProjectParticipants(project);
                      const participantCount = project.participantes?.length || 0;
                      
                      return (
                        <div
                          key={project.id}
                          className={`relative flex items-center gap-3 p-3 rounded-xl border transition-all ${
                            isEnrolled || canViewParticipants
                              ? "bg-card hover:shadow-md hover:border-primary/30 cursor-pointer" 
                              : canEnroll
                              ? "bg-card hover:shadow-md hover:border-primary/30 cursor-pointer"
                              : "bg-muted/30"
                          }`}
                          onClick={() => {
                            // Membro inscrito: Abre Área do Membro
                            if (isEnrolled && !canViewParticipants) {
                              setStudentAreaProject(project);
                              setStudentAreaModalOpen(true);
                              return;
                            }
                            // Líder: Navega direto para gerenciamento
                            if (canViewParticipants) {
                              navigate(`/social`, { state: { openProjectId: project.id } });
                              return;
                            }
                            // Não inscrito: Vai para inscrição
                            if (canEnroll) {
                              navigate(`/social/inscricao/${project.id}`);
                            }
                          }}
                          data-testid={`project-card-${project.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-sm text-foreground line-clamp-1">{project.nome}</h3>
                            {project.objetivo && (
                              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                                {project.objetivo}
                              </p>
                            )}
                            {/* Show participant count only for leaders/admins */}
                            {canViewParticipants && participantCount > 0 && (
                              <p className="text-xs text-blue-600 mt-0.5 flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {participantCount} inscrito{participantCount !== 1 ? 's' : ''}
                              </p>
                            )}
                          </div>
                          
                          <div className="flex-shrink-0 flex items-center gap-2">
                            {canViewParticipants && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="h-7 text-xs px-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/social`, { state: { openProjectId: project.id } });
                                }}
                              >
                                <Users className="h-3 w-3 mr-1" />
                                Ver
                              </Button>
                            )}
                            {isEnrolled ? (
                              <Badge 
                                className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs cursor-pointer hover:bg-green-200"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setStudentAreaProject(project);
                                  setStudentAreaModalOpen(true);
                                }}
                              >
                                {isOracao ? "Meus Pedidos" : "Área do Membro"}
                              </Badge>
                            ) : canEnroll ? (
                              <Button 
                                size="sm" 
                                className={`${typeColors.bg} hover:opacity-90 text-white shadow-sm h-7 text-xs px-3`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/social/inscricao/${project.id}`);
                                }}
                              >
                                {isOracao ? "Pedir" : "Inscrever"}
                              </Button>
                            ) : (
                              <Badge variant="outline" className="text-muted-foreground text-xs">
                                Ativo
                              </Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              );
            });
          })()}
        </div>
      )}

      {/* Schedule Detail Dialog */}
      <Dialog open={scheduleDetailDialog} onOpenChange={setScheduleDetailDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col p-0">
          <DialogHeader className="flex-shrink-0 p-6 pb-2">
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              {selectedSchedule?.evento_nome || selectedSchedule?.nome}
            </DialogTitle>
          </DialogHeader>
          {selectedSchedule && (
            <div className="flex-1 overflow-y-auto px-6 pb-6" style={{ maxHeight: 'calc(85vh - 100px)' }}>
              <div className="space-y-4 py-2">
                <div className="flex items-center gap-4 p-3 rounded-xl bg-muted/50">
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-primary/10 flex flex-col items-center justify-center">
                    <span className="text-xs text-muted-foreground uppercase">
                      {selectedSchedule.evento_data ? format(parseDate(selectedSchedule.evento_data), "MMM", { locale: ptBR }) : "---"}
                    </span>
                    <span className="text-xl font-bold text-primary">
                      {selectedSchedule.evento_data ? format(parseDate(selectedSchedule.evento_data), "dd") : "--"}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium">{selectedSchedule.evento_nome}</p>
                    <p className="text-sm text-muted-foreground">{selectedSchedule.evento_horario}</p>
                    {selectedSchedule.minha_funcao && (
                      <Badge className="mt-1">{selectedSchedule.minha_funcao}</Badge>
                    )}
                  </div>
                </div>

                {/* People */}
                {selectedSchedule.pessoas?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Equipe Escalada
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedSchedule.pessoas.map((p, idx) => (
                        <Badge key={idx} variant="secondary">
                          {p.person_nome}
                          <span className="text-muted-foreground ml-1">({p.function_nome})</span>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Songs - Layout igual à lista de músicas */}
                {selectedSchedule.musicas?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                      <Music className="h-4 w-4" />
                      Músicas
                    </h4>
                    <div className="space-y-2">
                      {selectedSchedule.musicas.map((m, idx) => (
                        <div 
                          key={idx} 
                          className="group flex items-center gap-3 p-3 rounded-xl bg-muted/30 hover:bg-muted/60 cursor-pointer transition-all"
                          onClick={() => handleSongClick(m.song_id)}
                        >
                          {/* Thumbnail com gradiente */}
                          <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center shadow-sm transform group-hover:scale-105 transition-transform ${
                            idx % 4 === 0 ? 'bg-gradient-to-br from-violet-500 to-purple-600' :
                            idx % 4 === 1 ? 'bg-gradient-to-br from-blue-500 to-cyan-500' :
                            idx % 4 === 2 ? 'bg-gradient-to-br from-emerald-500 to-teal-500' :
                            'bg-gradient-to-br from-orange-500 to-amber-500'
                          }`}>
                            <Music className="h-5 w-5 text-white drop-shadow" />
                          </div>
                          
                          {/* Song Info */}
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-foreground truncate block group-hover:text-primary transition-colors">
                              {m.song_nome}
                            </span>
                            {m.versao_nome && (
                              <span className="text-sm text-muted-foreground">{m.versao_nome}</span>
                            )}
                            <div className="flex items-center gap-1.5 mt-1">
                              {m.tom && (
                                <Badge variant="outline" className="text-xs px-2 py-0 h-5 font-medium">
                                  {m.tom}
                                </Badge>
                              )}
                            </div>
                          </div>

                          {/* Links Icons */}
                          <div className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                            {m.link_cifra && (
                              <a href={m.link_cifra} target="_blank" rel="noopener noreferrer" title="Cifra">
                                <div className="w-8 h-8 rounded-lg bg-amber-500/10 hover:bg-amber-500 flex items-center justify-center transition-colors group/icon">
                                  <BookOpen className="h-4 w-4 text-amber-600 group-hover/icon:text-white transition-colors" />
                                </div>
                              </a>
                            )}
                            {m.link_letra && (
                              <a href={m.link_letra} target="_blank" rel="noopener noreferrer" title="Letra">
                                <div className="w-8 h-8 rounded-lg bg-blue-500/10 hover:bg-blue-500 flex items-center justify-center transition-colors group/icon">
                                  <FileText className="h-4 w-4 text-blue-600 group-hover/icon:text-white transition-colors" />
                                </div>
                              </a>
                            )}
                            {m.link_video && (
                              <a href={m.link_video} target="_blank" rel="noopener noreferrer" title="Vídeo">
                                <div className="w-8 h-8 rounded-lg bg-red-500/10 hover:bg-red-500 flex items-center justify-center transition-colors group/icon">
                                  <Video className="h-4 w-4 text-red-600 group-hover/icon:text-white transition-colors" />
                                </div>
                              </a>
                            )}
                            <ChevronRight className="h-4 w-4 text-muted-foreground ml-1 group-hover:translate-x-0.5 transition-transform" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Observações */}
                {selectedSchedule.observacoes && (
                  <div>
                    <h4 className="text-sm font-medium mb-2">Observações</h4>
                    <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                      {selectedSchedule.observacoes}
                    </p>
                  </div>
                )}

                <div className="flex gap-2 pt-4 sticky bottom-0 bg-background">
                  <Button variant="outline" className={canAccessSchedulesPage() ? "flex-1" : "w-full"} onClick={() => setScheduleDetailDialog(false)}>
                    Fechar
                  </Button>
                  {canAccessSchedulesPage() && (
                    <Button className="flex-1 gap-2" onClick={handleGoToSchedules}>
                      <ExternalLink className="h-4 w-4" />
                      Ir para Escalas
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Birthdays */}
      {showBirthdaysWidget && stats.aniversariantes_lista?.length > 0 && (
        <Card data-testid="aniversariantes-widget">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Cake className="h-5 w-5 text-pink-500" />
              Aniversariantes do Mês
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {stats.aniversariantes_lista.map((person, idx) => (
                <Badge key={idx} variant="secondary" className="py-2 px-3">
                  {person.nome_completo}
                  {person.data_nascimento && (
                    <span className="ml-2 text-muted-foreground">
                      {format(parseDate(person.data_nascimento), "dd/MM")}
                    </span>
                  )}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Song Detail Dialog */}
      <Dialog open={songDetailDialog} onOpenChange={setSongDetailDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Music className="h-5 w-5 text-purple-500" />
              {selectedSong?.nome}
            </DialogTitle>
          </DialogHeader>
          {selectedSong && (
            <ScrollArea className="flex-1 -mx-6 px-6">
              <div className="space-y-4 py-4">
                {selectedSong.autor && (
                  <div>
                    <p className="text-sm text-muted-foreground">Autor</p>
                    <p className="font-medium">{selectedSong.autor}</p>
                  </div>
                )}
                
                {/* Versions */}
                {selectedSong.versoes && selectedSong.versoes.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground">Versões</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {selectedSong.versoes.map((v, idx) => (
                        <Badge key={idx} variant="secondary">
                          {v.nome} - Tom: {v.tom}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-3 gap-4">
                  {selectedSong.bpm && (
                    <div>
                      <p className="text-sm text-muted-foreground">BPM</p>
                      <p className="font-medium">{selectedSong.bpm}</p>
                    </div>
                  )}
                  {selectedSong.capotraste && (
                    <div>
                      <p className="text-sm text-muted-foreground">Capotraste</p>
                      <p className="font-medium">{selectedSong.capotraste}</p>
                    </div>
                  )}
                  {selectedSong.versao && (
                    <div>
                      <p className="text-sm text-muted-foreground">Versão</p>
                      <p className="font-medium">{selectedSong.versao}</p>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  {selectedSong.link_letra && (
                    <a href={selectedSong.link_letra} target="_blank" rel="noopener noreferrer">
                      <Badge className="bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 cursor-pointer">
                        <FileText className="h-3 w-3 mr-1" />
                        Ver Letra
                      </Badge>
                    </a>
                  )}
                  {selectedSong.link_video && (
                    <a href={selectedSong.link_video} target="_blank" rel="noopener noreferrer">
                      <Badge className="bg-red-500/10 text-red-600 hover:bg-red-500/20 cursor-pointer">
                        <Video className="h-3 w-3 mr-1" />
                        Ver Vídeo
                      </Badge>
                    </a>
                  )}
                  {selectedSong.link_cifra && (
                    <a href={selectedSong.link_cifra} target="_blank" rel="noopener noreferrer">
                      <Badge className="bg-green-500/10 text-green-600 hover:bg-green-500/20 cursor-pointer">
                        <BookOpen className="h-3 w-3 mr-1" />
                        Ver Cifra
                      </Badge>
                    </a>
                  )}
                </div>

                {selectedSong.arranjos && (
                  <div>
                    <p className="text-sm text-muted-foreground">Arranjos</p>
                    <p className="text-sm whitespace-pre-wrap">{selectedSong.arranjos}</p>
                  </div>
                )}

                {selectedSong.observacoes && (
                  <div>
                    <p className="text-sm text-muted-foreground">Letra / Observações</p>
                    <p className="text-sm whitespace-pre-wrap font-mono bg-muted/50 p-3 rounded-lg max-h-60 overflow-auto">
                      {selectedSong.observacoes}
                    </p>
                  </div>
                )}

                <Button variant="outline" className="w-full" onClick={() => setSongDetailDialog(false)}>
                  Fechar
                </Button>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

      {/* Project Participants Dialog */}
      <Dialog open={participantsDialog} onOpenChange={setParticipantsDialog}>
        <DialogContent className="max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {selectedProjectForParticipants?.nome}
            </DialogTitle>
          </DialogHeader>
          {selectedProjectForParticipants && (
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-4 pr-4">
                {/* Project Info */}
                <div className="p-3 bg-muted/50 rounded-lg space-y-2">
                  {selectedProjectForParticipants.data_evento && (
                    <p className="text-sm flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      {formatDateBR(selectedProjectForParticipants.data_evento)}
                      {selectedProjectForParticipants.horario_evento && ` às ${selectedProjectForParticipants.horario_evento}`}
                    </p>
                  )}
                  {selectedProjectForParticipants.valor_inscricao > 0 && (
                    <p className="text-sm font-medium text-green-600">
                      Valor: R$ {selectedProjectForParticipants.valor_inscricao.toFixed(2)}
                    </p>
                  )}
                </div>

                {/* Participant Count */}
                <div className="flex items-center justify-between">
                  <span className="font-medium">Inscritos</span>
                  <Badge>{selectedProjectForParticipants.participantes?.length || 0} pessoas</Badge>
                </div>

                {/* Participants List */}
                {selectedProjectForParticipants.participantes?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedProjectForParticipants.participantes.map((p, idx) => (
                      <div key={p.pessoa_id || idx} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{p.pessoa_nome || "Participante"}</p>
                          {p.telefone && (
                            <p className="text-xs text-muted-foreground">{p.telefone}</p>
                          )}
                          <p className="text-xs text-muted-foreground">
                            {p.data_inscricao ? formatDateBR(p.data_inscricao) : "Data não informada"}
                          </p>
                        </div>
                        {p.forma_pagamento && (
                          <Badge variant="outline" className="text-xs">
                            {p.forma_pagamento}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    Nenhum inscrito ainda
                  </p>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => {
                      setParticipantsDialog(false);
                      navigate(`/social/inscricao/${selectedProjectForParticipants.id}`);
                    }}
                  >
                    Fazer Inscrição
                  </Button>
                  <Button 
                    className="flex-1"
                    onClick={() => {
                      setParticipantsDialog(false);
                      navigate(`/social`, { state: { openProjectId: selectedProjectForParticipants.id } });
                    }}
                  >
                    Gerenciar Projeto
                  </Button>
                </div>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

      {/* Student/Member Area Modal */}
      <StudentAreaModal
        open={studentAreaModalOpen}
        onOpenChange={setStudentAreaModalOpen}
        projectId={studentAreaProject?.id}
        projectTitle={studentAreaProject?.nome}
        projectType={studentAreaProject?.tipo}
      />
    </div>
  );
}
